import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  DollarSign, Save, Building2, Calendar, RefreshCw, CheckCircle, Percent, Tag, Info
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';

import API from '@/config/api';
// API imported from config;

const ChildBillingSettings = ({ childId, childName, onUpdate }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    tuition_schedule: 'weekly',
    tuition_amount: '',
    registration_fee: '',
    registration_frequency: 'biannual', // yearly or biannual
    registration_date_1: '', // First billing date (MM-DD format or full date)
    registration_date_2: '', // Second billing date (for biannual)
    state_assisted: false,
    state_agency: '',
    case_number: '',
    copay_amount: '',
    auto_invoice: false,
    // Discount settings
    discount_enabled: false,
    discount_type: 'flat', // 'flat' or 'percent'
    discount_amount: '',
    discount_reason: '', // 'sibling', 'employee', 'scholarship', 'military', 'custom'
    discount_notes: ''
  });
  const [feesSettings, setFeesSettings] = useState(null);
  const [siblingSync, setSiblingSync] = useState(null); // { available, source, siblings }
  const [selectedSyncIds, setSelectedSyncIds] = useState([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (childId) {
      fetchSettings();
      fetchSiblingSyncPreview();
    }
  }, [childId]);

  const fetchSiblingSyncPreview = async () => {
    try {
      const r = await axios.get(`${API}/children/${childId}/billing/sibling-sync-preview`, getAuthHeaders());
      setSiblingSync(r.data);
      if (r.data?.available && r.data?.siblings) {
        // Default: pre-select siblings that don't have a tuition amount set yet
        setSelectedSyncIds(r.data.siblings.filter(s => !s.current_tuition_amount).map(s => s.child_id));
      }
    } catch (e) {
      setSiblingSync(null);
    }
  };

  const handleSyncToSiblings = async () => {
    if (!selectedSyncIds.length) return;
    setSyncing(true);
    try {
      const r = await axios.post(
        `${API}/children/${childId}/billing/sync-siblings`,
        { target_child_ids: selectedSyncIds },
        getAuthHeaders()
      );
      toast.success(`Copied to ${r.data.synced_count} sibling${r.data.synced_count !== 1 ? 's' : ''}`);
      // Refresh so the sync state disappears
      fetchSiblingSyncPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [billingRes, feesRes, childRes] = await Promise.all([
        axios.get(`${API}/children/${childId}/billing`, getAuthHeaders()),
        axios.get(`${API}/settings/fees`, getAuthHeaders()).catch(() => ({ data: {} })),
        axios.get(`${API}/children`, getAuthHeaders()).then(res => {
          const child = res.data.find(c => c.id === childId);
          return { data: child || {} };
        }).catch(() => ({ data: {} }))
      ]);
      
      const firstAttendanceDate = childRes.data.first_attendance_date || '';
      const date1 = billingRes.data.registration_date_1 || firstAttendanceDate;
      const freq = billingRes.data.registration_frequency || 'biannual';
      
      // Auto-calculate second date as 6 months from first if not already set
      let date2 = billingRes.data.registration_date_2 || '';
      if (!date2 && date1 && freq === 'biannual') {
        const d = new Date(date1 + 'T00:00:00');
        d.setMonth(d.getMonth() + 6);
        date2 = d.toISOString().split('T')[0];
      }
      
      setSettings({
        tuition_schedule: billingRes.data.tuition_schedule || 'weekly',
        tuition_amount: billingRes.data.tuition_amount || '',
        registration_fee: billingRes.data.registration_fee || '',
        registration_frequency: freq,
        registration_date_1: date1,
        registration_date_2: date2,
        state_assisted: billingRes.data.state_assisted || false,
        state_agency: billingRes.data.state_agency || '',
        case_number: billingRes.data.case_number || '',
        copay_amount: billingRes.data.copay_amount || '',
        auto_invoice: billingRes.data.auto_invoice || false,
        // Discount settings
        discount_enabled: billingRes.data.discount_enabled || false,
        discount_type: billingRes.data.discount_type || 'flat',
        discount_amount: billingRes.data.discount_amount || '',
        discount_reason: billingRes.data.discount_reason || '',
        discount_notes: billingRes.data.discount_notes || ''
      });
      
      setFeesSettings(feesRes.data);
    } catch (error) {
      console.error('Failed to load billing settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/children/${childId}/billing`, settings, getAuthHeaders());
      toast.success('Billing settings saved');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to save billing settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="child-billing-settings">
      {/* Sibling sync banner: primary child has a payment-agreement price */}
      {siblingSync?.available && (siblingSync.siblings || []).length > 0 && (
        <Card className="border-2 border-primary/40 bg-primary/5" data-testid="sibling-sync-banner">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  <p className="font-semibold text-sm">Apply this family's rate to siblings</p>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  <strong>{siblingSync.source.child_name}</strong>'s signed agreement is
                  <strong> ${siblingSync.source.price_amount}</strong> per {siblingSync.source.price_period}.
                  Tick the siblings you want to bill the same rate.
                </p>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {siblingSync.siblings.map(sib => {
                    const checked = selectedSyncIds.includes(sib.child_id);
                    const hasExisting = !!sib.current_tuition_amount;
                    return (
                      <label
                        key={sib.child_id}
                        className={`flex items-center gap-2 p-2 rounded border text-xs cursor-pointer ${
                          checked ? 'bg-white border-primary' : 'bg-white/60 border-gray-200 hover:border-primary'
                        }`}
                        data-testid={`sibling-sync-${sib.child_id}`}
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-primary"
                          checked={checked}
                          onChange={(e) => {
                            setSelectedSyncIds(prev =>
                              e.target.checked
                                ? [...prev, sib.child_id]
                                : prev.filter(x => x !== sib.child_id)
                            );
                          }}
                        />
                        <span className="flex-1 font-medium">{sib.name}</span>
                        {hasExisting && (
                          <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                            currently ${sib.current_tuition_amount}/{sib.current_tuition_schedule || 'wk'} — will overwrite
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              <Button
                size="sm"
                onClick={handleSyncToSiblings}
                disabled={syncing || !selectedSyncIds.length}
                className="whitespace-nowrap"
                data-testid="sibling-sync-apply"
              >
                {syncing ? 'Applying…' : `Apply to ${selectedSyncIds.length} sibling${selectedSyncIds.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tuition Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-500" />
            Tuition Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Billing Schedule</Label>
              <Select 
                value={settings.tuition_schedule} 
                onValueChange={(v) => setSettings(prev => ({ ...prev, tuition_schedule: v }))}
              >
                <SelectTrigger data-testid="tuition-schedule-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Custom Tuition Amount (optional)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Use default from Settings"
                  className="pl-8"
                  value={settings.tuition_amount}
                  onChange={(e) => setSettings(prev => ({ ...prev, tuition_amount: e.target.value }))}
                  data-testid="tuition-amount-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Leave blank to use age-based rate from Settings</p>
            </div>
          </div>

          {/* Registration Fee Section */}
          <div className="p-4 border rounded-lg bg-amber-50/50 border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-amber-600" />
              <Label className="font-semibold">Registration Fee Billing</Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label className="text-sm">Custom Amount (optional)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Use default from Settings"
                    className="pl-8"
                    value={settings.registration_fee}
                    onChange={(e) => setSettings(prev => ({ ...prev, registration_fee: e.target.value }))}
                    data-testid="registration-fee-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Billing Frequency</Label>
                <Select 
                  value={settings.registration_frequency} 
                  onValueChange={(v) => {
                    const updates = { registration_frequency: v };
                    // Auto-set second date when switching to biannual
                    if (v === 'biannual' && settings.registration_date_1) {
                      const d = new Date(settings.registration_date_1 + 'T00:00:00');
                      d.setMonth(d.getMonth() + 6);
                      updates.registration_date_2 = d.toISOString().split('T')[0];
                    }
                    setSettings(prev => ({ ...prev, ...updates }));
                  }}
                >
                  <SelectTrigger data-testid="registration-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">Yearly (1x per year)</SelectItem>
                    <SelectItem value="biannual">Bi-Annual (2x per year)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">
                  {settings.registration_frequency === 'biannual' ? 'First Billing Date' : 'Billing Date'}
                </Label>
                <Input
                  type="date"
                  value={settings.registration_date_1}
                  onChange={(e) => {
                    const newDate1 = e.target.value;
                    const updates = { registration_date_1: newDate1 };
                    // Auto-set second date to 6 months from first when biannual
                    if (settings.registration_frequency === 'biannual' && newDate1) {
                      const d = new Date(newDate1 + 'T00:00:00');
                      d.setMonth(d.getMonth() + 6);
                      updates.registration_date_2 = d.toISOString().split('T')[0];
                    }
                    setSettings(prev => ({ ...prev, ...updates }));
                  }}
                  data-testid="registration-date-1"
                />
                <p className="text-xs text-muted-foreground">Defaults to first day of attendance</p>
              </div>
              
              {settings.registration_frequency === 'biannual' && (
                <div className="space-y-2">
                  <Label className="text-sm">Second Billing Date</Label>
                  <Input
                    type="date"
                    value={settings.registration_date_2}
                    onChange={(e) => setSettings(prev => ({ ...prev, registration_date_2: e.target.value }))}
                    data-testid="registration-date-2"
                  />
                  <p className="text-xs text-muted-foreground">Auto-set to 6 months from first date (editable)</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-blue-500" />
              <div>
                <p className="font-medium text-sm">Auto-Generate Invoices</p>
                <p className="text-xs text-muted-foreground">Automatically create invoices based on schedule</p>
              </div>
            </div>
            <Switch
              checked={settings.auto_invoice}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_invoice: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Discount Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="w-4 h-4 text-purple-500" />
            Discount Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
            <div>
              <p className="font-medium text-sm">Apply Discount</p>
              <p className="text-xs text-muted-foreground">Give this child a discount on tuition</p>
            </div>
            <Switch
              checked={settings.discount_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, discount_enabled: checked }))}
              data-testid="discount-enabled-switch"
            />
          </div>

          {settings.discount_enabled && (
            <div className="space-y-4 p-4 border border-purple-200 rounded-lg bg-purple-50/50">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount Type</Label>
                  <Select 
                    value={settings.discount_type} 
                    onValueChange={(v) => setSettings(prev => ({ ...prev, discount_type: v }))}
                  >
                    <SelectTrigger data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat Amount ($)</SelectItem>
                      <SelectItem value="percent">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{settings.discount_type === 'percent' ? 'Discount Percentage' : 'Discount Amount'}</Label>
                  <div className="relative">
                    {settings.discount_type === 'percent' ? (
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    ) : (
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    )}
                    <Input
                      type="number"
                      step={settings.discount_type === 'percent' ? '1' : '0.01'}
                      min="0"
                      max={settings.discount_type === 'percent' ? '100' : undefined}
                      placeholder={settings.discount_type === 'percent' ? '15' : '50.00'}
                      className={settings.discount_type === 'percent' ? 'pr-8' : 'pl-8'}
                      value={settings.discount_amount}
                      onChange={(e) => setSettings(prev => ({ ...prev, discount_amount: e.target.value }))}
                      data-testid="discount-amount-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Discount Reason</Label>
                <Select 
                  value={settings.discount_reason} 
                  onValueChange={(v) => setSettings(prev => ({ ...prev, discount_reason: v }))}
                >
                  <SelectTrigger data-testid="discount-reason-select">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sibling">Sibling Discount</SelectItem>
                    <SelectItem value="employee">Employee Discount</SelectItem>
                    <SelectItem value="scholarship">Scholarship</SelectItem>
                    <SelectItem value="military">Military Discount</SelectItem>
                    <SelectItem value="referral">Referral Discount</SelectItem>
                    <SelectItem value="promotional">Promotional Discount</SelectItem>
                    <SelectItem value="custom">Custom / Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Discount Notes (optional)</Label>
                <Textarea
                  placeholder="Additional notes about this discount..."
                  value={settings.discount_notes}
                  onChange={(e) => setSettings(prev => ({ ...prev, discount_notes: e.target.value }))}
                  data-testid="discount-notes-input"
                  className="h-20"
                />
              </div>

              {/* Effective Rate Preview */}
              {(settings.tuition_amount || feesSettings) && settings.discount_amount && (
                <div className="p-3 bg-white rounded-lg border border-purple-300">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-4 h-4 text-purple-600" />
                    <p className="font-medium text-sm text-purple-800">Effective Rate Preview</p>
                  </div>
                  {(() => {
                    const baseRate = parseFloat(settings.tuition_amount) || 0;
                    const discount = parseFloat(settings.discount_amount) || 0;
                    let discountValue = 0;
                    let effectiveRate = baseRate;
                    
                    if (settings.discount_type === 'percent') {
                      discountValue = (baseRate * discount) / 100;
                      effectiveRate = baseRate - discountValue;
                    } else {
                      discountValue = discount;
                      effectiveRate = baseRate - discount;
                    }
                    
                    effectiveRate = Math.max(0, effectiveRate);
                    
                    if (baseRate > 0) {
                      return (
                        <div className="text-sm space-y-1">
                          <p className="text-muted-foreground">
                            Base Rate: <span className="font-medium">${baseRate.toFixed(2)}/{settings.tuition_schedule}</span>
                          </p>
                          <p className="text-red-600">
                            Discount: <span className="font-medium">-${discountValue.toFixed(2)}</span>
                            {settings.discount_type === 'percent' && ` (${discount}%)`}
                          </p>
                          <p className="text-green-700 font-semibold text-base pt-1 border-t border-purple-200">
                            Effective Rate: ${effectiveRate.toFixed(2)}/{settings.tuition_schedule}
                          </p>
                        </div>
                      );
                    }
                    return (
                      <p className="text-xs text-muted-foreground">
                        Set a custom tuition amount above to see the effective rate
                      </p>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* State Assistance Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-500" />
            State Assistance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <p className="font-medium text-sm">State Assisted Program</p>
              <p className="text-xs text-muted-foreground">Child receives state childcare assistance</p>
            </div>
            <Switch
              checked={settings.state_assisted}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, state_assisted: checked }))}
              data-testid="state-assisted-switch"
            />
          </div>

          {settings.state_assisted && (
            <div className="space-y-4 p-4 border border-blue-200 rounded-lg bg-blue-50/50">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>State Agency</Label>
                  <Select 
                    value={settings.state_agency} 
                    onValueChange={(v) => setSettings(prev => ({ ...prev, state_agency: v }))}
                  >
                    <SelectTrigger data-testid="state-agency-select">
                      <SelectValue placeholder="Select agency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wisconsin_shares">Wisconsin Shares</SelectItem>
                      <SelectItem value="w2">W-2 (Wisconsin Works)</SelectItem>
                      <SelectItem value="head_start">Head Start</SelectItem>
                      <SelectItem value="early_head_start">Early Head Start</SelectItem>
                      <SelectItem value="4k">4K Program</SelectItem>
                      <SelectItem value="county_assistance">County Assistance</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Case Number / Authorization #</Label>
                  <Input
                    type="text"
                    placeholder="Enter case number"
                    value={settings.case_number}
                    onChange={(e) => setSettings(prev => ({ ...prev, case_number: e.target.value }))}
                    data-testid="case-number-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Parent Co-Pay Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Amount parent pays after state portion"
                    className="pl-8"
                    value={settings.copay_amount}
                    onChange={(e) => setSettings(prev => ({ ...prev, copay_amount: e.target.value }))}
                    data-testid="copay-amount-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  The amount the parent is responsible for after state assistance
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-billing-settings-btn">
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Billing Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ChildBillingSettings;
